/**
 * engineNormal_Merge_TemplateTraversal.ts - Slot and placeholder traversal for EngineNormal
 * Port of C# EngineNormal_Merge_TemplateTraversal.cs
 * Pure ES Module version
 */

import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { CommonUtil } from '../../common/commonUtil.js';
import { getRecursionDepthError, collectUnresolvedMarkers } from './engineNormal_Helper.js';
import type { EngineNormal } from './engineNormal.js';
import type { JsonObject } from '../../app/json/jsonValue.js';
import { JsonValue } from '../../app/json/jsonValue.js';
import type { TemplateOperation } from '../../model/templateOperation.js';
import { TemplateOperationKind } from '../../model/templateOperationKind.js';
import type { PhaseTiming } from '../../base/timing/phaseTiming.js';
import { TimingPhase } from '../../base/timing/timingPhase.js';

type GetTemplateWithJsonFn = (appSite: string, currentTemplateName: string, placeholderName: string, appView: string | null, flowStack: string[] | null, configJson: JsonObject | null) => { html: string | null; json: JsonObject | null; resolvedTemplateName: string | null; resolvedAppSite: string | null };
type MergeJsonIntoTemplateFn = (html: string, json: JsonObject | null, placeholderName: string, resolvedTemplateName: string | null, resolvedAppSite: string | null, operations: TemplateOperation[], contextJson: JsonObject | null, configJson: JsonObject | null) => string;
type ReplaceTemplatePlaceholdersFn = (engine: EngineNormal, html: string, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: TemplateOperation[], depth: number, componentHtml: JsonObject, genComponentHtml: boolean, contextJson: JsonObject | null, flowStack: string[], getTemplateWithJson: GetTemplateWithJsonFn, mergeJsonIntoTemplate: MergeJsonIntoTemplateFn, timing: PhaseTiming, configJson?: JsonObject | null) => string;

interface SlotContentResult {
    processedContent: string;
    operations: TemplateOperation[];
}

export const templateTraversalMethods = {
    /**
     * IndexOf-based version: Recursively merges a slotted template (e.g., center.html, columns.html) with content.html
     * Slot patterns in content.html: {{#TemplateName}} ... {{@HTMLPLACEHOLDER[N]}} ... {{/HTMLPLACEHOLDER[N]}} ... {{/TemplateName}}
     */
    mergeTemplateSlots(
        this: EngineNormal,
        contentHtml: string,
        appSite: string,
        currentTemplateName: string,
        appView: string | null,
        templateKey: string,
        operations: TemplateOperation[],
        depth: number,
        componentHtml: JsonObject,
        genComponentHtml: boolean,
        contextJson: JsonObject | null,
        flowStack: string[],
        getTemplateWithJson: GetTemplateWithJsonFn,
        mergeJsonIntoTemplate: MergeJsonIntoTemplateFn,
        replaceTemplatePlaceholders: ReplaceTemplatePlaceholdersFn,
        timing: PhaseTiming,
        configJson: JsonObject | null = null
    ): string {
        const recursionDepthError = getRecursionDepthError(this, depth, templateKey);
        if (recursionDepthError !== null) {
            return recursionDepthError;
        }

        if (!contentHtml) {
            return contentHtml;
        }

        let previous: string;
        let result = contentHtml;
        const maxIterations = this._loader!.maxMergeIterations;
        let stabilized = false;
        for (let iteration = 0; iteration < maxIterations; iteration++) {
            previous = result;
            result = this.processTemplateSlots(
                result,
                appSite,
                currentTemplateName,
                appView,
                templateKey,
                operations,
                depth,
                componentHtml,
                genComponentHtml,
                contextJson,
                flowStack,
                getTemplateWithJson,
                mergeJsonIntoTemplate,
                replaceTemplatePlaceholders,
                timing,
                configJson
            );
            if (result === previous) {
                stabilized = true;
                break;
            }
        }

        // A slot expansion that keeps re-emitting slot markers never settles. The depth guard above bounds
        // nesting but not iteration, so without this the loop spins forever on a circular slot reference.
        if (!stabilized) {
            throw new Error(
                `Slot merge did not stabilize after ${maxIterations} iterations for appSite=${appSite} ` +
                `templateKey=${templateKey}. Unresolved markers: ${collectUnresolvedMarkers(result)}. ` +
                'Possible circular slot reference.');
        }

        return result;
    },

    /**
     * Helper method to process slotted templates using IndexOf
     */
    processTemplateSlots(
        this: EngineNormal,
        contentHtml: string,
        appSite: string,
        currentTemplateName: string,
        appView: string | null,
        templateKey: string,
        operations: TemplateOperation[],
        depth: number,
        componentHtml: JsonObject,
        genComponentHtml: boolean,
        contextJson: JsonObject | null,
        flowStack: string[],
        getTemplateWithJson: GetTemplateWithJsonFn,
        mergeJsonIntoTemplate: MergeJsonIntoTemplateFn,
        replaceTemplatePlaceholders: ReplaceTemplatePlaceholdersFn,
        timing: PhaseTiming,
        configJson: JsonObject | null = null
    ): string {
        let result = contentHtml;
        let searchPos = 0;

        while (searchPos < result.length) {
            let openStart = result.indexOf('{{#', searchPos);
            const openStartSkip = result.indexOf('{{!#', searchPos);

            let isSkipMarked = false;
            if (openStartSkip !== -1 && (openStart === -1 || openStartSkip < openStart)) {
                openStart = openStartSkip;
                isSkipMarked = true;
            }

            if (openStart === -1) {
                break;
            }

            const nameOffset = isSkipMarked ? 4 : 3;
            const openEnd = result.indexOf('}}', openStart + nameOffset);
            if (openEnd === -1) {
                break;
            }

            const templateName = result.substring(openStart + nameOffset, openEnd).trim();
            if (!templateName || !CommonUtil.isAlphaNumeric(templateName)) {
                searchPos = openStart + 1;
                continue;
            }

            const closeTag = '{{/' + templateName + '}}';
            const openTag = (isSkipMarked ? '{{!#' : '{{#') + templateName + '}}';
            const closeStart = CommonUtil.findMatchingCloseTag(result, openEnd + 2, openTag, closeTag);
            if (closeStart === -1) {
                searchPos = openStart + 1;
                continue;
            }

            const innerStart = openEnd + 2;
            const innerContent = result.substring(innerStart, closeStart);
            const nestedOperations: TemplateOperation[] = [];
            const templateNameWithMarker = isSkipMarked ? '!' + templateName : templateName;

            flowStack.push(templateName);

            Logger.flow(`ProcessTemplateSlots: slot=${templateName} skipMarked=${isSkipMarked}`, "Normal.SlotExpansion");
            let { html: templateHtml, json: templateJson, resolvedTemplateName, resolvedAppSite: resolvedSlotAppSite } =
                getTemplateWithJson(appSite, currentTemplateName, templateNameWithMarker, appView, flowStack, configJson);
            Logger.flow(`ProcessTemplateSlots: slot=${templateName} resolved=${resolvedTemplateName == null ? 'null' : resolvedTemplateName} resolvedSize=${templateHtml == null ? 0 : templateHtml.length}`, "Normal.SlotExpansion");

            if (resolvedTemplateName && resolvedTemplateName !== templateName && flowStack.length > 0) {
                flowStack[flowStack.length - 1] = resolvedTemplateName;
            }

            if (templateHtml !== null) {
                const replacedWith = `${resolvedSlotAppSite}_${resolvedTemplateName || templateName}`;
                let processedTemplate: string;
                const isSlotSkippedOrMissing =
                    resolvedTemplateName === this._loader!.defaultSkipTemplateName ||
                    resolvedTemplateName === this._loader!.defaultReplaceTemplateName;

                if (templateHtml !== '' && !isSkipMarked && !isSlotSkippedOrMissing) {
                    let modifiedTemplateHtml = templateHtml;
                    modifiedTemplateHtml = mergeJsonIntoTemplate(
                        modifiedTemplateHtml,
                        templateJson,
                        templateName,
                        resolvedTemplateName,
                        resolvedSlotAppSite,
                        nestedOperations,
                        contextJson,
                        configJson
                    );

                    if (genComponentHtml) {
                        componentHtml.set(templateNameWithMarker, JsonValue.string(''));
                    }

                    const slotContents = this.extractSlotContents(
                        innerContent,
                        appSite,
                        currentTemplateName,
                        appView,
                        replacedWith,
                        depth,
                        componentHtml,
                        genComponentHtml,
                        contextJson,
                        flowStack,
                        getTemplateWithJson,
                        mergeJsonIntoTemplate,
                        replaceTemplatePlaceholders,
                        timing,
                        configJson
                    );

                    processedTemplate = modifiedTemplateHtml;
                    for (const [slotKey, { processedContent, operations: slotOps }] of Object.entries(slotContents)) {
                        if (!processedTemplate.includes(slotKey)) {
                            continue;
                        }

                        processedTemplate = processedTemplate.replace(slotKey, processedContent);
                        const slotName = slotKey.replace('{{$', '').replace('}}', '');
                        nestedOperations.push({
                            templateName: replacedWith,
                            kind: TemplateOperationKind.SlotMerge,
                            target: slotKey,
                            replacedWith: slotName,
                            operations: slotOps
                        } as TemplateOperation);
                    }

                    processedTemplate = CommonUtil.removeRemainingSlotPlaceholders(processedTemplate);
                    const nestedTemplateKey = replacedWith;
                    processedTemplate = replaceTemplatePlaceholders(
                        this,
                        processedTemplate,
                        appSite,
                        templateName,
                        appView,
                        nestedTemplateKey,
                        nestedOperations,
                        depth + 1,
                        componentHtml,
                        genComponentHtml,
                        contextJson,
                        flowStack,
                        getTemplateWithJson,
                        mergeJsonIntoTemplate,
                        timing,
                        configJson
                    );
                } else {
                    processedTemplate = templateHtml;
                }

                if (genComponentHtml) {
                    componentHtml.set(templateNameWithMarker, JsonValue.string(processedTemplate));
                }

                result = timing.measure(TimingPhase.Assemble, () => result.substring(0, openStart) + processedTemplate + result.substring(closeStart + closeTag.length));

                let opType: TemplateOperationKind = TemplateOperationKind.SlotReplace;
                if (resolvedTemplateName === this._loader!.defaultSkipTemplateName) {
                    opType = TemplateOperationKind.SlotSkip;
                } else if (resolvedTemplateName === this._loader!.defaultReplaceTemplateName) {
                    opType = TemplateOperationKind.SlotMissing;
                }

                const targetMarker = isSkipMarked ? '{{!#' + templateName + '}}' : '{{#' + templateName + '}}';
                operations.push({
                    templateName: templateKey,
                    kind: opType,
                    target: targetMarker,
                    replacedWith,
                    operations: nestedOperations
                } as TemplateOperation);

                searchPos = openStart + processedTemplate.length;
            } else {
                searchPos = openStart + 1;
            }

            flowStack.pop();
        }

        return result;
    },

    /**
     * Extract slot contents using IndexOf approach
     * Returns dictionary of slot key to (processed content, operations) tuple
     */
    extractSlotContents(
        this: EngineNormal,
        innerContent: string,
        appSite: string,
        currentTemplateName: string,
        appView: string | null,
        parentTemplateKey: string,
        depth: number,
        componentHtml: JsonObject,
        genComponentHtml: boolean,
        contextJson: JsonObject | null,
        flowStack: string[],
        getTemplateWithJson: GetTemplateWithJsonFn,
        mergeJsonIntoTemplate: MergeJsonIntoTemplateFn,
        replaceTemplatePlaceholders: ReplaceTemplatePlaceholdersFn,
        timing: PhaseTiming,
        configJson: JsonObject | null = null
    ): Record<string, SlotContentResult> {
        const slotContents: Record<string, SlotContentResult> = {};
        let searchPos = 0;

        while (searchPos < innerContent.length) {
            const slotStart = innerContent.indexOf('{{@HTMLPLACEHOLDER', searchPos);
            if (slotStart === -1) {
                break;
            }

            const afterPlaceholder = slotStart + 18;
            const slotNumStart = afterPlaceholder;
            let pos = afterPlaceholder;

            while (pos < innerContent.length) {
                const code = innerContent.charCodeAt(pos);
                if (code < 48 || code > 57) {
                    break;
                }
                pos++;
            }
            const slotNum = pos === slotNumStart ? '' : innerContent.substring(slotNumStart, pos);

            if (pos + 1 >= innerContent.length || innerContent.charCodeAt(pos) !== 125 || innerContent.charCodeAt(pos + 1) !== 125) {
                searchPos = slotStart + 1;
                continue;
            }

            const slotOpenEnd = pos + 2;
            const closeTag = slotNum === '' ? '{{/HTMLPLACEHOLDER}}' : `{{/HTMLPLACEHOLDER${slotNum}}}`;
            const openTag = slotNum === '' ? '{{@HTMLPLACEHOLDER}}' : `{{@HTMLPLACEHOLDER${slotNum}}}`;
            const closeStart = CommonUtil.findMatchingCloseTag(innerContent, slotOpenEnd, openTag, closeTag);
            if (closeStart === -1) {
                searchPos = slotStart + 1;
                continue;
            }

            const slotContent = innerContent.substring(slotOpenEnd, closeStart);
            const slotKey = slotNum === '' ? '{{$HTMLPLACEHOLDER}}' : `{{$HTMLPLACEHOLDER${slotNum}}}`;
            const slotContentOperations: TemplateOperation[] = [];

            let recursiveResult = this.mergeTemplateSlots(
                slotContent,
                appSite,
                currentTemplateName,
                appView,
                parentTemplateKey,
                slotContentOperations,
                depth + 1,
                componentHtml,
                genComponentHtml,
                contextJson,
                flowStack,
                getTemplateWithJson,
                mergeJsonIntoTemplate,
                replaceTemplatePlaceholders,
                timing,
                configJson
            );
            recursiveResult = replaceTemplatePlaceholders(
                this,
                recursiveResult,
                appSite,
                currentTemplateName,
                appView,
                parentTemplateKey,
                slotContentOperations,
                depth + 1,
                componentHtml,
                genComponentHtml,
                contextJson,
                flowStack,
                getTemplateWithJson,
                mergeJsonIntoTemplate,
                timing,
                configJson
            );

            slotContents[slotKey] = { processedContent: recursiveResult, operations: slotContentOperations };
            searchPos = closeStart + closeTag.length;
        }

        return slotContents;
    }
};

/**
 * Helper method to process simple placeholders only (without slotted template processing)
 */
export function replaceTemplatePlaceholders(engine: EngineNormal, html: string, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: TemplateOperation[], depth: number, componentHtml: JsonObject, genComponentHtml: boolean, contextJson: JsonObject | null, flowStack: string[], getTemplateWithJson: GetTemplateWithJsonFn, mergeJsonIntoTemplate: MergeJsonIntoTemplateFn, timing: PhaseTiming, configJson: JsonObject | null = null): string {
    const recursionDepthError = getRecursionDepthError(engine, depth, templateKey);
    if (recursionDepthError !== null) {
        return recursionDepthError;
    }

    let result = html;
    let searchPos = 0;

    while (searchPos < result.length) {
        // Look for opening placeholder {{
        const openStart = result.indexOf('{{', searchPos);
        if (openStart === -1) break;

        // Make sure it's not a slotted template ({{#) or special placeholder ({{@, {{$, {{/)
        // Note: {{! is allowed as it's a skip marker for placeholders, not slotted templates ({{!#)
        if (openStart + 2 < result.length && (result[openStart + 2] === '#' || result[openStart + 2] === '@' || result[openStart + 2] === '$' || result[openStart + 2] === '/')) {
            searchPos = openStart + 2;
            continue;
        }

        // Find closing }}
        const closeStart = result.indexOf('}}', openStart + 2);
        if (closeStart === -1) break;

        // Extract placeholder name (may start with ! for skip marker)
        const placeholderName = result.substring(openStart + 2, closeStart).trim();
        if (!placeholderName || !CommonUtil.isAlphaNumeric(placeholderName)) {
            searchPos = openStart + 2;
            continue;
        }

        // Create nested operations list BEFORE loading template (for JSON key tracking)
        const nestedOperations: TemplateOperation[] = [];

        // Get clean placeholder name (without ! marker)
        const cleanPlaceholderName = placeholderName.startsWith('!') ? placeholderName.substring(1) : placeholderName;

        // Push onto flow stack for inheritance resolution (pop after recursive processing)
        flowStack.push(cleanPlaceholderName);

        Logger.flow(`ReplaceTemplatePlaceholders: placeholder=${placeholderName} depth=${depth}`, "Normal.TemplateRecursion");
        // Load template with JSON on-demand - pass currentTemplateName for component reuse pattern detection
        // GetTemplate will handle skip marker (!) if present
        let { html: templateContent, json: templateJson, resolvedTemplateName, resolvedAppSite: resolvedComponentAppSite } = getTemplateWithJson(appSite, currentTemplateName, placeholderName, appView, flowStack, configJson);
        Logger.flow(`ReplaceTemplatePlaceholders: placeholder=${placeholderName} resolved=${resolvedTemplateName == null ? 'null' : resolvedTemplateName} resolvedSize=${templateContent == null ? 0 : templateContent.length}`, "Normal.TemplateRecursion");

        // Update flow stack top with resolved template name for correct inheritance lookup
        if (resolvedTemplateName && resolvedTemplateName !== cleanPlaceholderName && flowStack.length > 0) {
            flowStack[flowStack.length - 1] = resolvedTemplateName;
        }

        // Check if template was found (templateContent can be empty for skip markers)
        if (templateContent !== null) {
            // Use resolved AppSite and template name for operations tracking (handles SearchAppSites and AppView fallback)
            const replacedWith = `${resolvedComponentAppSite}_${resolvedTemplateName || placeholderName}`;

            // Recursively process the loaded template - now placeholderName is the current template
            // Process BOTH slotted templates AND placeholders to track all nested operations
            // Skip processing if templateContent is empty (skip marker case)
            const nestedTemplateKey = replacedWith;
            // Check if this is a skip-marked or missing template
            const isPlaceholderSkipMarked = placeholderName.startsWith('!');
            const isSkippedOrMissing = resolvedTemplateName === engine._loader!.defaultSkipTemplateName || resolvedTemplateName === engine._loader!.defaultReplaceTemplateName;

            let processedReplacement: string;
            if (templateContent !== '' && !isPlaceholderSkipMarked && !isSkippedOrMissing) {
                Logger.flow(`ReplaceTemplatePlaceholders: branch=process placeholder=${placeholderName} viewHtmlSize=${templateContent.length}`, "Normal.TemplateRecursion");
                let modifiedTemplateContent = templateContent;

                // Merge JSON AFTER DynamicViewHandlers so {{$KeyName}} patterns get wrapped first
                modifiedTemplateContent = mergeJsonIntoTemplate(modifiedTemplateContent, templateJson, cleanPlaceholderName, resolvedTemplateName, resolvedComponentAppSite, nestedOperations, contextJson, configJson);
                Logger.flow(`ReplaceTemplatePlaceholders: postJsonMerge placeholder=${placeholderName} templateHtmlSize=${modifiedTemplateContent.length}`, "Normal.TemplateRecursion");

                // Add pre-order entry for placeholder component BEFORE processing (parent before children)
                // Keep ! prefix so skip components are identifiable (matches JSON engines)
                if (genComponentHtml) {
                    componentHtml.set(placeholderName, JsonValue.string(''));
                }

                processedReplacement = engine.mergeTemplateSlots(modifiedTemplateContent, appSite, placeholderName, appView, nestedTemplateKey, nestedOperations, depth + 1, componentHtml, genComponentHtml, contextJson, flowStack, getTemplateWithJson, mergeJsonIntoTemplate, replaceTemplatePlaceholders, timing, configJson);
                Logger.flow(`ReplaceTemplatePlaceholders: postMergeTemplateSlots placeholder=${placeholderName} afterSlotsSize=${processedReplacement.length}`, "Normal.TemplateRecursion");
                processedReplacement = replaceTemplatePlaceholders(engine, processedReplacement, appSite, placeholderName, appView, nestedTemplateKey, nestedOperations, depth + 1, componentHtml, genComponentHtml, contextJson, flowStack, getTemplateWithJson, mergeJsonIntoTemplate, timing, configJson);
                Logger.flow(`ReplaceTemplatePlaceholders: postRecursivePh placeholder=${placeholderName} processedReplacementSize=${processedReplacement.length}`, "Normal.TemplateRecursion");

            } else {
                processedReplacement = templateContent; // Empty string for skip markers
            }

            // Store processed template in componentHtml (update pre-order entry with actual content)
            // Keep ! prefix so skip components are identifiable (matches JSON engines)
            if (genComponentHtml) {
                componentHtml.set(placeholderName, JsonValue.string(processedReplacement));
            }

            // Replace the placeholder at the current position only
            result = timing.measure(TimingPhase.Assemble, () => result.substring(0, openStart) + processedReplacement + result.substring(closeStart + 2));

            // Determine operation type based on resolvedTemplateName
            let opType: TemplateOperationKind = TemplateOperationKind.ComponentReplace;
            if (resolvedTemplateName === engine._loader!.defaultSkipTemplateName) {
                opType = TemplateOperationKind.ComponentSkip;
            } else if (resolvedTemplateName === engine._loader!.defaultReplaceTemplateName) {
                opType = TemplateOperationKind.ComponentMissing;
            }

            // Add operation with nested operations
            operations.push({
                templateName: templateKey,
                kind: opType,
                target: '{{' + placeholderName + '}}',
                replacedWith: replacedWith,
                operations: nestedOperations
            } as TemplateOperation);

            searchPos = openStart + processedReplacement.length;
        } else {
            searchPos = closeStart + 2;
        }

        // Pop the flow stack entry we pushed at the start of this iteration
        flowStack.pop();
    }

    return result;
}
